import TransactionCard from "@/pages/main/components/transaction-card";
import { useTranslation } from "react-i18next";
import Loading from "@/components/custom/loading";
import { useMemo, useState } from "react";
import { useGetCurrencies } from "@/services/queries/currency";
import { RiRefreshLine } from "@remixicon/react";
import { useGetVendors } from "@/services/queries/vendor";
import { Transaction } from "@/lib/types";
import DeleteTransactionDialog from "../../components/delete-transaction-dialog";

const INITIAL_TRANSACTION_COUNT = 3;

const Transactions = ({
  transactions,
  isPending,
  refetch,
}: {
  transactions: Transaction[];
  isPending: boolean;
  refetch: () => void;
}) => {
  const { t, i18n } = useTranslation();
  const [showAll, setShowAll] = useState(false);
  const { data: currenciesData } = useGetCurrencies();
  const { data: vendorsData } = useGetVendors();
  const [selectedTransaction, setSelectedTransaction] =
    useState<Transaction | null>(null);
  const [visible, setVisible] = useState(false);

  const displayedTransactions = showAll
    ? transactions
    : transactions.slice(0, INITIAL_TRANSACTION_COUNT);
  const hasMore = transactions.length > INITIAL_TRANSACTION_COUNT;

  const currencies = useMemo(() => {
    return currenciesData?.value;
  }, [currenciesData]);

  const vendors = useMemo(() => {
    return vendorsData?.value;
  }, [vendorsData]);

  return (
    <div className="mt-5 flex flex-col items-center justify-center px-5">
      <div className="flex w-full flex-row items-center justify-between">
        <h2 className="w-full text-start text-[15px] font-semibold leading-[15px] tracking-wide text-white">
          {t("title.transactions").toLocaleUpperCase(i18n.language)}
        </h2>
        <button
          onClick={() => {
            refetch();
          }}
          className="text-white"
          data-testid="refresh-button"
        >
          <RiRefreshLine size={18} />
        </button>
      </div>

      <div className="justify-centerpt-5 flex min-h-[120px] w-full flex-col items-center justify-center pt-3">
        {isPending ? (
          <Loading />
        ) : (
          <>
            {transactions.length > 0 && (
              <div className="flex h-full w-full flex-1 flex-col items-start gap-[10px]">
                {hasMore && showAll && (
                  <button
                    type="button"
                    onClick={() => setShowAll(false)}
                    className="w-full rounded-md border border-white/30 bg-white/10 py-2 text-sm font-medium text-white"
                    data-testid="show-less-transactions"
                  >
                    {t("navigation.less")}
                  </button>
                )}
                {renderTransactions(displayedTransactions)}
                {hasMore && !showAll && (
                  <button
                    type="button"
                    onClick={() => setShowAll(true)}
                    className="w-full rounded-md border border-white/30 bg-white/10 py-2 text-sm font-medium text-white"
                    data-testid="show-more-transactions"
                  >
                    {t("navigation.more")}
                  </button>
                )}
              </div>
            )}

            {transactions.length === 0 && (
              <div className="flex w-full flex-col items-center justify-center gap-[10px]">
                <p className="text-white">{t("label.no_expenses_found")}</p>
              </div>
            )}
          </>
        )}
      </div>
      <DeleteTransactionDialog
        visible={visible}
        setVisible={setVisible}
        id={selectedTransaction?.id}
        refetch={refetch}
        onSuccess={() => {
          // The dialog already runs setVisible(false) + refetch() in its
          // mutation onSuccess; only the selected-transaction reset is ours.
          setSelectedTransaction(null);
        }}
      />
    </div>
  );

  function renderTransactions(list: Transaction[]) {
    if (list.length <= 0) return null;

    return list.map((transaction) => (
      <TransactionCard
        key={transaction.id}
        currencies={currencies}
        transaction={transaction}
        vendors={vendors}
        onDelete={(transaction) => {
          setSelectedTransaction(transaction);
          setVisible(true);
        }}
      />
    ));
  }
};

export default Transactions;
